import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  ChevronRight,
  Mail,
  MessageSquare,
  Phone,
  Search,
  Sparkles,
  Star,
  Zap,
  Shield,
  Users,
  BarChart3,
  Layers,
  Cpu,
  DollarSign,
  Rocket,
  HelpCircle,
  GitCompare,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";

import "../styleguide/os2-theme.css";
import { SectionTitleSlide } from "../styleguide/SlideKit";

import EmailGate from "../components/EmailGate";
import {
  buildContentTrackingMetadata,
  getStoredContentSessionId,
} from "../lib/gatedContentTracking";
import AgentTitleSlide from "../components/slides/AgentTitleSlide";
import AIReceptionistSlide from "../components/slides/AIReceptionistSlide";
import AlainaSlide from "../components/slides/AlainaSlide";
import ArielSlide from "../components/slides/ArielSlide";
import AshaVsDentinaReceptionistSlide from "../components/slides/AshaVsDentinaReceptionistSlide";
import AshaVsDentinaSlide from "../components/slides/AshaVsDentinaSlide";
import AshaVsWeaveSlide from "../components/slides/AshaVsWeaveSlide";
import BackingSlide from "../components/slides/BackingSlide";
import AshaVsEassistSlide from "../components/slides/AshaVsEassistSlide";
import PMSPricingComparisonSlide from "../components/slides/PMSPricingComparisonSlide";
import AshaOperatingSystemSlide from "../components/slides/AshaOperatingSystemSlide";
import AshaBasicallyFreeSlide from "../components/slides/AshaBasicallyFreeSlide";
import PriceMatchGuaranteeSlide from "../components/slides/PriceMatchGuaranteeSlide";
import TotalPricingComparisonSlide from "../components/slides/TotalPricingComparisonSlide";
import BenefitFormsSlide from "../components/slides/BenefitFormsSlide";
import CaseStudySlide from "../components/slides/CaseStudySlide";
import CommunicationSuiteSlide from "../components/slides/CommunicationSuiteSlide";
import MarketingTitleSlide from "../components/slides/MarketingTitleSlide";
import SMSCampaignsSlide from "../components/slides/SMSCampaignsSlide";
import EmailCampaignsSlide from "../components/slides/EmailCampaignsSlide";
import CommunicationTitleSlide from "../components/slides/CommunicationTitleSlide";
import ComparisonsTitleSlide from "../components/slides/ComparisonsTitleSlide";
import AshaVsLassieSlide from "../components/slides/AshaVsLassieSlide";
import CompetitorAnalysisSlide from "../components/slides/CompetitorAnalysisSlide";
import CommunicationSuiteComparisonSlide from "../components/slides/CommunicationSuiteComparisonSlide";
import ComprehensiveBreakdownsSlide from "../components/slides/ComprehensiveBreakdownsSlide";
import EFAXSlide from "../components/slides/EFAXSlide";
import ElisePatientExperienceSlide from "../components/slides/ElisePatientExperienceSlide";
import MorganPatientIntakeSlide from "../components/slides/MorganPatientIntakeSlide";
import PracticeManagerSlide from "../components/slides/PracticeManagerSlide";
import FrontOfficeGettingStartedSlide from "../components/slides/FrontOfficeGettingStartedSlide";
import OnboardingTimelineSlide from "../components/slides/OnboardingTimelineSlide";
import GettingStartedSlide from "../components/slides/GettingStartedSlide";
import ContractTermsSlide from "../components/slides/ContractTermsSlide";
import WhiteGloveOnboardingSlide from "../components/slides/WhiteGloveOnboardingSlide";
import GoLiveTimelineSlide from "../components/slides/GoLiveTimelineSlide";
import DataMigrationSlide from "../components/slides/DataMigrationSlide";
import FAQSlide from "../components/slides/FAQSlide";
import HumanBillerSlide from "../components/slides/HumanBillerSlide";
import ConsolidationSlide from "../components/slides/ConsolidationSlide";
import ManualTasksSlide from "../components/slides/ManualTasksSlide";
import HygieneRecallSlide from "../components/slides/HygieneRecallSlide";
import InsuranceCallingSlide from "../components/slides/InsuranceCallingSlide";
import InsuranceCopilotSlide from "../components/slides/InsuranceCopilotSlide";
import InsuranceVerificationSlide from "../components/slides/InsuranceVerificationSlide";
import IntelligentSchedulingSlide from "../components/slides/IntelligentSchedulingSlide";
import KimCaseAcceptanceSlide from "../components/slides/KimCaseAcceptanceSlide";
import LenaSlide from "../components/slides/LenaSlide";
import LenaWorkflowSlide from "../components/slides/LenaWorkflowSlide";
import NextGenFormsSlide from "../components/slides/NextGenFormsSlide";
import RealROISlide from "../components/slides/RealROISlide";
import ROIIntroSlide from "../components/slides/ROIIntroSlide";
import VoIPSlide from "../components/slides/VoIPSlide";
import PhysicalPhonesSlide from "../components/slides/PhysicalPhonesSlide";
import TextToPaySlide from "../components/slides/TextToPaySlide";
import CardReadersSlide from "../components/slides/CardReadersSlide";
import TeamChatSlide from "../components/slides/TeamChatSlide";
import PMSSchedulingSlide from "../components/slides/PMSSchedulingSlide";
import PMSChartingSlideAnimated from "../components/slides/PMSChartingSlideAnimated";
import PMSTreatmentPlanningSlide from "../components/slides/PMSTreatmentPlanningSlide";
import PMSClinicalNotesSlide from "../components/slides/PMSClinicalNotesSlide";
import PMSFileManagementSlide from "../components/slides/PMSFileManagementSlide";
import PMSImagingSlide from "../components/slides/PMSImagingSlide";
import PMSAITreatmentPlannerSlide from "../components/slides/PMSAITreatmentPlannerSlide";
import PMSRevenueAnalyticsSlide from "../components/slides/PMSRevenueAnalyticsSlide";
import PMSAIScribeSlide from "../components/slides/PMSAIScribeSlide";
import PMSAshaCopilotSlide from "../components/slides/PMSAshaCopilotSlide";

import EOBProcessingSlide from "../components/slides/EOBProcessingSlide";
import EOBStreamliningSlide from "../components/slides/EOBStreamliningSlide";
import PatientBillingSlide from "../components/slides/PatientBillingSlide";
import PaymentReconciliationSlide from "../components/slides/PaymentReconciliationSlide";

import PMSOfferingsSlide from "../components/slides/PMSOfferingsSlide";
import PMSDemoSlide from "../components/slides/PMSDemoSlide";
import PMSTeamIntro1Slide from "../components/slides/PMSTeamIntro1Slide";
import PMSTeamIntro2Slide from "../components/slides/PMSTeamIntro2Slide";
import SoftwareConsolidationSlide from "../components/slides/SoftwareConsolidationSlide";
import AIWorkforceSlide from "../components/slides/AIWorkforceSlide";
import AIROISlide from "../components/slides/AIROISlide";
import AIClinicSlide from "../components/slides/AIClinicSlide";

import PatientJourneySlide from "../components/slides/PatientJourneySlide";
import SoftwareStackCostSlide from "../components/slides/SoftwareStackCostSlide";
import RCMPricingComparisonSlide from "../components/slides/RCMPricingComparisonSlide";
import EngagementModelsSlide from "../components/slides/EngagementModelsSlide";
import ManagedServiceSlide from "../components/slides/ManagedServiceSlide";
import FlexiblePlanSlide from "../components/slides/FlexiblePlanSlide";
import AIOSEmphasisSlide from "../components/slides/AIOSEmphasisSlide";
import TotalCostCalculatorSlide from "../components/slides/TotalCostCalculatorSlide";
import ROICalculatorSlide from "../components/slides/ROICalculatorSlide";

// Section icons for the sidebar
const sectionIcons = {
  "welcome": null,
  "why-asha": null,
  "backing": null,
  "overview": <Layers className="w-3.5 h-3.5" />,
  "pms-features": <Cpu className="w-3.5 h-3.5" />,
  "ai-team-1": <Users className="w-3.5 h-3.5" />,
  "comms-suite": <MessageSquare className="w-3.5 h-3.5" />,
  "ai-team-2": <BarChart3 className="w-3.5 h-3.5" />,
  "pricing": <DollarSign className="w-3.5 h-3.5" />,
  "getting-started": <Rocket className="w-3.5 h-3.5" />,
  "faq": <HelpCircle className="w-3.5 h-3.5" />,
  "roi": <Zap className="w-3.5 h-3.5" />,
  "comparisons": <GitCompare className="w-3.5 h-3.5" />,
};

const osSlides = [
  { id: 0, slug: "welcome", title: "Welcome", indent: 0 },
  { id: 1, slug: "why-asha", title: "Why Asha?", indent: 0 },
  { id: 2, slug: "backing", title: "Backing & Team", indent: 0 },
  // Overview
  { id: 300, slug: "overview", title: "Overview", indent: 0 },
  { id: 157, slug: "consolidation", title: "What Your Software Handles Today", indent: 1 },
  { id: 158, slug: "manual-tasks", title: "What Your Staff Handles Today", indent: 1 },
  { id: 303, slug: "software-consolidation", title: "10+ Softwares \u2192 1 Platform", indent: 1 },
  { id: 304, slug: "ai-workforce", title: "AI Handles Staff Tasks", indent: 1 },
  { id: 99, slug: "offerings", title: "AI-Native PMS", indent: 1 },
  { id: 302, slug: "ai-roi", title: "How AI Drives ROI", indent: 1 },
  { id: 305, slug: "ai-clinic", title: "10 Softwares, 20 AI Products", indent: 1 },
  // PMS Features
  { id: 400, slug: "pms-features", title: "PMS Features", indent: 0 },
  { id: 401, slug: "pms-scheduling", title: "Smart Scheduling", indent: 1 },
  { id: 402, slug: "pms-charting", title: "Clinical Charting", indent: 1 },
  { id: 403, slug: "pms-treatment-planning", title: "Treatment Planning", indent: 1 },
  { id: 404, slug: "pms-clinical-notes", title: "Clinical Notes", indent: 1 },
  { id: 405, slug: "pms-file-management", title: "Document Management", indent: 1 },
  { id: 406, slug: "pms-imaging", title: "Digital Imaging", indent: 1 },
  { id: 407, slug: "pms-ai-treatment-planner", title: "AI Treatment Planner", indent: 1 },
  { id: 408, slug: "pms-revenue-analytics", title: "Revenue Analytics Pro", indent: 1 },
  { id: 409, slug: "pms-ai-scribe", title: "AI Scribe", indent: 1 },
  { id: 410, slug: "pms-asha-copilot", title: "Asha Copilot", indent: 1 },
  // Front Office AI Team
  { id: 3, slug: "ai-team-1", title: "Front Office AI Team", indent: 0 },
  { id: 4, slug: "amy", title: "Amy, Receptionist", indent: 1 },
  { id: 5, slug: "amy-features", title: "Features", indent: 2 },
  { id: 6, slug: "amy-scheduling", title: "Intelligent Scheduling", indent: 2 },
  { id: 7, slug: "leena", title: "Leena, Schedule Optimizer", indent: 1 },
  { id: 8, slug: "leena-features", title: "Features", indent: 2 },
  { id: 9, slug: "leena-workflow", title: "12-Step Workflow", indent: 2 },
  { id: 10, slug: "stephanie", title: "Stephanie, Hygiene Recall", indent: 1 },
  { id: 11, slug: "stephanie-features", title: "Features", indent: 2 },
  { id: 110, slug: "kim", title: "Kim, Case Acceptance", indent: 1 },
  { id: 111, slug: "kim-revenue", title: "Revenue Automation", indent: 2 },
  { id: 112, slug: "elise", title: "Elise, Patient Experience", indent: 1 },
  { id: 113, slug: "elise-reviews", title: "Review Generation", indent: 2 },
  { id: 114, slug: "morgan", title: "Morgan, Patient Intake", indent: 1 },
  { id: 115, slug: "morgan-intake", title: "Intake & Insurance", indent: 2 },
  { id: 116, slug: "practice-manager", title: "Trisha, Practice Manager", indent: 1 },
  { id: 117, slug: "practice-manager-dashboard", title: "Task Dashboard", indent: 2 },
  // Communication Suite
  { id: 17, slug: "comms-suite", title: "Communication Suite", indent: 0 },
  { id: 18, slug: "voip", title: "VoIP", indent: 1 },
  { id: 29, slug: "forms", title: "Next-Gen Forms", indent: 1 },
  { id: 19, slug: "messaging", title: "2-Way Messaging", indent: 1 },
  { id: 20, slug: "fax", title: "Intelligent Fax", indent: 1 },
  { id: 201, slug: "sms-campaigns", title: "SMS Campaigns", indent: 1 },
  { id: 202, slug: "email-campaigns", title: "Email Campaigns", indent: 1 },
  { id: 2041, slug: "physical-phones", title: "Physical Phones", indent: 1 },
  { id: 2051, slug: "text-to-pay", title: "Text-to-Pay", indent: 1 },
  { id: 206, slug: "card-readers", title: "Card Readers", indent: 1 },
  { id: 207, slug: "team-chat", title: "Team Chat", indent: 1 },
  // Revenue Cycle AI Team
  { id: 22, slug: "ai-team-2", title: "Revenue Cycle AI Team", indent: 0 },
  { id: 12, slug: "amanda", title: "Amanda, Insurance Verification", indent: 1 },
  { id: 13, slug: "amanda-checks", title: "Automatic Checks", indent: 2 },
  { id: 30, slug: "amanda-breakdowns", title: "Comprehensive Breakdowns", indent: 2 },
  { id: 31, slug: "amanda-copilot", title: "AI Insurance Co-Pilot", indent: 2 },
  { id: 16, slug: "amanda-forms", title: "Automated Forms", indent: 2 },
  { id: 15, slug: "amanda-calling", title: "AI Calling", indent: 2 },
  { id: 23, slug: "ariel", title: "Ariel, Claims Expert", indent: 1 },
  { id: 24, slug: "ariel-submission", title: "Claim Submission", indent: 2 },
  { id: 101, slug: "ariel-scrubbing", title: "Claim Scrubbing", indent: 2 },
  { id: 100, slug: "alisha", title: "Alisha, EOB Posting", indent: 1 },
  { id: 25, slug: "alisha-posting", title: "Automated Posting", indent: 2 },
  { id: 102, slug: "alisha-reconciliation", title: "Payment Reconciliation", indent: 2 },
  { id: 103, slug: "alisha-ingestion", title: "Unified Ingestion", indent: 2 },
  { id: 26, slug: "kianna", title: "Kianna, Patient Billing", indent: 1 },
  { id: 104, slug: "kianna-campaigns", title: "Voice Campaigns", indent: 2 },
  { id: 27, slug: "human-biller", title: "Human Biller", indent: 1 },
  // Pricing & Packaging
  { id: 150, slug: "pricing", title: "Pricing & Packaging", indent: 0 },
  { id: 600, slug: "patient-journey", title: "What Your Staff Does", indent: 1 },
  { id: 601, slug: "software-stack", title: "Your Software Stack", indent: 1 },
  { id: 606, slug: "ai-os-emphasis", title: "The Asha AI OS", indent: 1 },
  { id: 203, slug: "asha-operating-system", title: "Asha AI Operating System", indent: 1 },
  { id: 602, slug: "rcm-pricing-comparison", title: "RCM Pricing Comparison", indent: 1 },
  { id: 607, slug: "total-cost-calculator", title: "Total Cost to Piecemeal", indent: 1 },
  { id: 603, slug: "engagement-models", title: "Engagement Models", indent: 1 },
  { id: 604, slug: "managed-service", title: "Managed Service", indent: 1 },
  { id: 605, slug: "flexible-plan", title: "Flexible Plan", indent: 1 },
  { id: 608, slug: "roi-calculator", title: "ROI Calculator", indent: 1 },
  { id: 2042, slug: "asha-basically-free", title: "Asha Pays for Itself", indent: 1 },
  // Getting Started
  { id: 210, slug: "getting-started", title: "Getting Started", indent: 0 },
  { id: 211, slug: "go-live-timeline", title: "4-Week Go-Live", indent: 1 },
  { id: 156, slug: "data-migration", title: "Seamless Data Migration", indent: 1 },
  { id: 155, slug: "contract-terms", title: "Contract Terms & Guarantees", indent: 1 },
  { id: 109, slug: "white-glove-onboarding", title: "White Glove Onboarding", indent: 1 },
  // FAQ
  { id: 215, slug: "faq", title: "FAQ", indent: 0 },
  { id: 216, slug: "frequently-asked-questions", title: "Frequently Asked Questions", indent: 1 },
  // ROI & Impact
  { id: 120, slug: "roi", title: "ROI & Impact", indent: 0 },
  { id: 500, slug: "case-study-dental-art-care", title: "Dental Art Care Case Study", indent: 1 },
  { id: 501, slug: "case-study-clovis", title: "Clovis Family Case Study", indent: 1 },
  { id: 502, slug: "case-study-dds-spokane", title: "DDS Spokane Case Study", indent: 1 },
  { id: 503, slug: "case-study-st-charles", title: "St. Charles Case Study", indent: 1 },
  { id: 504, slug: "case-study-premier", title: "Premier Dentistry Case Study", indent: 1 },
  { id: 505, slug: "case-study-hudson-valley", title: "Dental Arts of Hudson Valley", indent: 1 },
  { id: 122, slug: "case-study-ntx", title: "NTX Mental Health Case Study", indent: 1 },
  { id: 123, slug: "roi-data", title: "Real Practice Data", indent: 1 },
  // Comparisons
  { id: 130, slug: "comparisons", title: "Comparisons", indent: 0 },
  { id: 131, slug: "vs-dentina-platform", title: "Asha vs Dentina: Platform", indent: 1 },
  { id: 133, slug: "vs-dentina-receptionist", title: "Asha vs Dentina: Receptionist", indent: 1 },
  { id: 132, slug: "vs-weave", title: "Asha vs Weave", indent: 1 },
  { id: 134, slug: "vs-lassie", title: "Asha vs Lassie", indent: 1 },
];

const OS2_ACCESS_KEY = "asha2026";

const OS2SlideshowPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Gate: require ?key=asha2026 to access this page
  if (searchParams.get("key") !== OS2_ACCESS_KEY) {
    return <Navigate to="/" replace />;
  }

  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [scale, setScale] = useState(1);
  const [isLocked, setIsLocked] = useState(true);
  const containerRef = React.useRef(null);
  const slideStartTimeRef = React.useRef(Date.now());
  const currentSlideRef = React.useRef(0);

  const recordSlideView = (slideIndex, duration, useBeacon = false) => {
    const sessionId = getStoredContentSessionId("asha_os_slideshow_session_id");
    if (!sessionId || duration < 1000) return;

    const slide = osSlides[slideIndex];
    const payload = JSON.stringify({
      sessionId,
      slideId: slide?.id ?? slideIndex,
      slideTitle: slide?.title ?? `Slide ${slideIndex}`,
      slideSlug: slide?.slug,
      durationSeconds: Math.round(duration / 1000),
      ...buildContentTrackingMetadata({ contentType: "slideshow" }),
    });

    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");
    const apiUrl =
      process.env.REACT_APP_API_URL ||
      (isLocalhost ? "http://localhost:8001" : "");
    const url = `${apiUrl}/api/slideshow/slide-view`;

    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch((err) => console.error("Failed to record slide view", err));
  };

  useEffect(() => {
    const hasAccess = localStorage.getItem("asha_os_slideshow_access");
    const sessionId = getStoredContentSessionId("asha_os_slideshow_session_id");
    if (hasAccess && sessionId) setIsLocked(false);
  }, []);

  const handleAccessGranted = (email) => {
    localStorage.setItem("asha_os_slideshow_access", "true");
    currentSlideRef.current = api?.selectedScrollSnap?.() ?? 0;
    slideStartTimeRef.current = Date.now();
    setIsLocked(false);
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.offsetWidth;
        const targetWidth = 1400;
        setScale(parentWidth / targetWidth);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      const newIndex = api.selectedScrollSnap();
      const now = Date.now();
      const duration = now - slideStartTimeRef.current;
      recordSlideView(currentSlideRef.current, duration);
      currentSlideRef.current = newIndex;
      slideStartTimeRef.current = now;
      setCurrent(newIndex + 1);
      const slideSlug = osSlides[newIndex]?.slug;
      if (slideSlug) setSearchParams({ slide: slideSlug }, { replace: true });
    });
    return () => {
      const now = Date.now();
      recordSlideView(currentSlideRef.current, now - slideStartTimeRef.current);
    };
  }, [api, setSearchParams]);

  useEffect(() => {
    const flushOnHide = () => {
      const now = Date.now();
      const duration = now - slideStartTimeRef.current;
      recordSlideView(currentSlideRef.current, duration, true);
      slideStartTimeRef.current = now;
    };
    window.addEventListener("pagehide", flushOnHide);
    return () => window.removeEventListener("pagehide", flushOnHide);
  }, []);

  useEffect(() => {
    if (!api) return;
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") api.scrollNext();
      else if (event.key === "ArrowLeft") api.scrollPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [api]);

  useEffect(() => {
    if (!api) return;
    const slideParam = searchParams.get("slide");
    if (slideParam) {
      let slideIndex = osSlides.findIndex((s) => s.slug === slideParam);
      if (slideIndex === -1) {
        const slideId = parseInt(slideParam, 10);
        if (!isNaN(slideId)) slideIndex = osSlides.findIndex((s) => s.id === slideId);
      }
      if (slideIndex !== -1) setTimeout(() => api.scrollTo(slideIndex), 100);
    }
  }, [api, searchParams]);

  const filteredSlides = osSlides
    .map((slide, index) => ({ ...slide, originalIndex: index }))
    .filter((slide) =>
      slide.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Group slides by section for sidebar
  const sections = [];
  let currentSection = null;
  filteredSlides.forEach((slide) => {
    if (slide.indent === 0) {
      currentSection = { ...slide, children: [] };
      sections.push(currentSection);
    } else if (currentSection) {
      currentSection.children.push(slide);
    }
  });

  return (
    <div className="os2-theme min-h-screen xl:h-screen w-screen bg-[#F1F5F9] flex flex-col items-center justify-start p-2 md:p-6 pt-4 xl:pt-4 overflow-y-auto xl:overflow-hidden overflow-x-hidden relative">
      <AnimatePresence>
        {isLocked && <EmailGate onAccessGranted={handleAccessGranted} sessionStorageKey="asha_os_slideshow_session_id" />}
      </AnimatePresence>

      {/* ─── Top Bar ─── */}
      <div className="w-full max-w-[1800px] flex justify-between items-center gap-3 mb-4 z-50">
        {/* Logo + Search */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5 px-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center p-1.5">
              <img src="/assets/alogo.png" alt="Asha" className="w-full h-full object-contain brightness-0 invert" />
            </div>
            <span className="text-sm font-bold text-slate-800 tracking-tight hidden md:block">Asha Health</span>
          </div>
          <div className="relative group w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search slides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg py-2 pl-9 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all shadow-sm"
            />
          </div>
        </div>

        {/* Slide counter + Actions */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 font-medium tabular-nums hidden md:block">
            {current} / {count || osSlides.length}
          </span>

          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 shadow-sm text-sm h-9"
              >
                I have questions
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle className="text-2xl font-bold text-slate-900 tracking-tight">
                  Get in Touch
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-base">
                  Have questions about Asha? We're here to help.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 p-6 pt-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-4 h-auto py-4 px-4 text-left border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 hover:shadow-md transition-all duration-200 group bg-white rounded-xl shadow-sm"
                  onClick={() => window.open("https://calendly.com/asha-health/dental-one-stop-shop", "_blank")}
                >
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 shrink-0 group-hover:scale-105 transition-transform duration-200 border border-blue-100">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-slate-900 text-base group-hover:text-blue-700 transition-colors">Book a meeting</span>
                    <span className="text-sm text-slate-500">Schedule time with our team</span>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-4 h-auto py-4 px-4 text-left border-slate-200 hover:border-green-300 hover:bg-green-50/30 hover:shadow-md transition-all duration-200 group bg-white rounded-xl shadow-sm"
                  onClick={() => (window.location.href = "sms:+19146779846")}
                >
                  <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-600 shrink-0 group-hover:scale-105 transition-transform duration-200 border border-green-100">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-slate-900 text-base group-hover:text-green-700 transition-colors">Text us</span>
                    <span className="text-sm font-bold text-slate-700">(914) 677-9846</span>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-4 h-auto py-4 px-4 text-left border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 hover:shadow-md transition-all duration-200 group bg-white rounded-xl shadow-sm"
                  onClick={() => (window.location.href = "mailto:onboarding@asha.health")}
                >
                  <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600 shrink-0 group-hover:scale-105 transition-transform duration-200 border border-purple-100">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-slate-900 text-base group-hover:text-purple-700 transition-colors">Email us</span>
                    <span className="text-sm font-bold text-slate-700">onboarding@asha.health</span>
                  </div>
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-semibold text-sm h-9">
                I'm ready to get started
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle className="text-2xl font-bold text-slate-900 tracking-tight">
                  Complete your purchase order
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-base">
                  Ready to transform your practice? Finalize your subscription below.
                </DialogDescription>
              </DialogHeader>
              <div className="p-6 pt-2 flex flex-col gap-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-sm text-blue-900 leading-relaxed">
                      Once you complete your purchase order, a member of our team will reach out within 24 hours to schedule your onboarding call.
                    </p>
                  </div>
                </div>
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-semibold h-12 text-lg rounded-xl"
                  onClick={() => window.open("https://buy.stripe.com/bJe6oI6FD9NV4Ku2z48AE0O", "_blank")}
                >
                  Complete Purchase <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="w-full max-w-[1800px] flex flex-col xl:flex-row gap-4 items-center justify-center flex-1 min-h-0">
        {/* ─── Sidebar ─── */}
        <div className="w-full xl:w-[250px] flex flex-col flex-1 xl:flex-none xl:h-full py-4 xl:pt-0 xl:pb-6 pr-0 xl:pr-0 order-2 xl:order-1 min-h-[300px]">
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm xl:h-full overflow-hidden">
            <ScrollArea className="h-full custom-scrollbar">
              <div className="py-2 px-1">
                {sections.map((section) => (
                  <div key={`${section.id}-${section.slug}`} className="mb-1">
                    {/* Section header */}
                    <button
                      onClick={() => api?.scrollTo(section.originalIndex)}
                      className={`group flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg transition-all duration-200 ${
                        current === section.originalIndex + 1
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      {sectionIcons[section.slug] && (
                        <span className={`shrink-0 ${current === section.originalIndex + 1 ? "text-blue-600" : "text-slate-400"}`}>
                          {sectionIcons[section.slug]}
                        </span>
                      )}
                      <span className={`text-[13px] font-semibold truncate ${
                        current === section.originalIndex + 1 ? "text-blue-700" : ""
                      }`}>
                        {section.title}
                      </span>
                    </button>

                    {/* Children */}
                    {section.children.map((child) => (
                      <button
                        key={`${child.id}-${child.slug}`}
                        onClick={() => api?.scrollTo(child.originalIndex)}
                        className={`group flex items-center gap-1.5 w-full text-left transition-all duration-200 rounded-md py-1.5 ${
                          current === child.originalIndex + 1
                            ? "text-blue-600"
                            : "text-slate-400 hover:text-slate-700"
                        }`}
                        style={{ paddingLeft: `${(child.indent || 1) * 16 + 12}px`, paddingRight: '12px' }}
                      >
                        {current === child.originalIndex + 1 && (
                          <div className="w-1 h-1 rounded-full bg-blue-500 shrink-0" />
                        )}
                        <span className={`text-[12px] truncate ${
                          current === child.originalIndex + 1
                            ? "font-medium text-blue-600"
                            : "font-normal"
                        }`}>
                          {child.title}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
                {filteredSlides.length === 0 && (
                  <div className="text-slate-400 text-sm italic py-4 px-4">No slides found</div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* ─── Main Slide Container ─── */}
        <div className="w-full xl:flex-1 max-w-[1400px] aspect-video relative order-1 xl:order-2 flex-shrink-0 group">
          <div
            ref={containerRef}
            className="w-full h-full overflow-hidden bg-transparent relative"
          >
            <div
              style={{
                width: "1400px",
                height: "787.5px",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Carousel setApi={setApi} className="w-full h-full">
                <div className="w-full h-full bg-white rounded-2xl overflow-hidden shadow-[0_4px_24px_-4px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.03)] relative isolate transform-gpu">
                  <CarouselContent className="h-full ml-0">

                    {/* ═══════════════════════════════════════════════
                        SLIDE 1: HERO — Clean BRIX-style
                    ═══════════════════════════════════════════════ */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 flex flex-col items-center justify-center text-white px-4 relative overflow-hidden">
                        {/* Decorative arcs */}
                        <div className="absolute top-[-20%] right-[-15%] w-[600px] h-[600px] rounded-full border border-white/10" />
                        <div className="absolute bottom-[-25%] left-[-10%] w-[700px] h-[700px] rounded-full border border-white/10" />
                        <div className="absolute top-[15%] left-[8%] w-[250px] h-[250px] rounded-full border border-white/5" />
                        <div className="absolute bottom-[20%] right-[5%] w-[150px] h-[150px] rounded-full border border-white/5" />

                        {/* Subtle grid */}
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:48px_48px]" />

                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className="text-center max-w-5xl z-10 relative"
                        >
                          {/* Dot pattern logo motif */}
                          <div className="mb-8 flex justify-center">
                            <div className="w-20 h-20 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 p-4">
                              <img
                                src="/assets/alogo.png"
                                alt="Asha Logo"
                                className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]"
                              />
                            </div>
                          </div>

                          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-blue-200 mb-4">
                            Welcome to Asha Health
                          </p>

                          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight">
                            The AI-Native Operating System for Dental
                          </h1>

                          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed mb-10 font-light">
                            20+ AI products and 10 software solutions consolidated into one intelligent platform
                          </p>

                          <Button
                            onClick={() => api?.scrollNext()}
                            size="lg"
                            className="bg-white text-blue-600 hover:bg-blue-50 font-semibold text-base px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                          >
                            Start Tour <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                        </motion.div>
                      </div>
                    </CarouselItem>

                    {/* ═══════════════════════════════════════════════
                        SLIDE 2: WHY ASHA — BRIX feature-card style
                    ═══════════════════════════════════════════════ */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-[#F8FAFC] flex flex-col items-center justify-center px-16 py-12 relative overflow-hidden">
                        {/* Decorative arcs */}
                        <div className="absolute top-[-10%] right-[-5%] w-[350px] h-[350px] rounded-full border-2 border-blue-100/30" />
                        <div className="absolute bottom-[-15%] left-[-8%] w-[450px] h-[450px] rounded-full border-2 border-blue-100/20" />

                        <div className="relative z-10 w-full max-w-5xl">
                          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-blue-600 mb-3">
                              Why Asha?
                            </p>
                            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-12">
                              Why practices choose Asha Health
                            </h2>
                          </motion.div>

                          <div className="grid grid-cols-2 gap-6">
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 }}
                              className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow"
                            >
                              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-5 border border-blue-100">
                                <Layers className="w-6 h-6" />
                              </div>
                              <h3 className="text-xl font-bold text-slate-900 mb-3">The only 1-stop shop</h3>
                              <p className="text-slate-500 leading-relaxed">
                                Not just an AI receptionist. A full team of front office + back office AI agents, communication suite, and PMS. No need for 10 different vendors.
                              </p>
                            </motion.div>

                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 }}
                              className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow"
                            >
                              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-5 border border-blue-100">
                                <Shield className="w-6 h-6" />
                              </div>
                              <h3 className="text-xl font-bold text-slate-900 mb-3">Strongest backing in dental AI</h3>
                              <p className="text-slate-500 leading-relaxed">
                                Most institutional capital raised, backed by Y-Combinator & General Catalyst. Built by Google AI product leaders & health system CMOs.
                              </p>
                            </motion.div>

                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3 }}
                              className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow"
                            >
                              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-5 border border-blue-100">
                                <Zap className="w-6 h-6" />
                              </div>
                              <h3 className="text-xl font-bold text-slate-900 mb-3">Real ROI from day one</h3>
                              <p className="text-slate-500 leading-relaxed">
                                Practices see an average $9,800/mo increase in production. Asha doesn't cost you — it pays for itself.
                              </p>
                            </motion.div>

                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.4 }}
                              className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow"
                            >
                              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-5 border border-blue-100">
                                <Rocket className="w-6 h-6" />
                              </div>
                              <h3 className="text-xl font-bold text-slate-900 mb-3">4-week go-live</h3>
                              <p className="text-slate-500 leading-relaxed">
                                White-glove onboarding with zero downtime migration. Dedicated implementation team and bi-weekly syncs.
                              </p>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>

                    {/* SLIDE 3: BACKING */}
                    <CarouselItem className="h-full pl-0">
                      <BackingSlide />
                    </CarouselItem>

                    {/* ═══ OVERVIEW SECTION ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        label="Platform Overview"
                        title="Understanding"
                        titleAccent="Today's Challenges"
                        subtitle="The current state of practice operations and how Asha transforms it"
                        icon={<Sparkles className="w-8 h-8" />}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><ConsolidationSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><ManualTasksSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><SoftwareConsolidationSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AIWorkforceSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0">
                      <PMSOfferingsSlide
                        onExplore={(type) => {
                          if (type === "front-office") {
                            const idx = osSlides.findIndex(s => s.slug === "ai-team-1");
                            if (idx !== -1) api?.scrollTo(idx);
                          } else if (type === "back-office") {
                            const idx = osSlides.findIndex(s => s.slug === "ai-team-2");
                            if (idx !== -1) api?.scrollTo(idx);
                          }
                        }}
                      />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><AIROISlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AIClinicSlide /></CarouselItem>

                    {/* ═══ PMS FEATURES SECTION ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        variant="blue"
                        label="Core Platform"
                        title="PMS Features"
                        subtitle="Everything you need to manage your dental practice"
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><PMSSchedulingSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSChartingSlideAnimated /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSTreatmentPlanningSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSClinicalNotesSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSFileManagementSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSImagingSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSAITreatmentPlannerSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSRevenueAnalyticsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSAIScribeSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PMSAshaCopilotSlide /></CarouselItem>

                    {/* ═══ FRONT OFFICE AI TEAM ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <PMSTeamIntro1Slide
                        onAgentClick={(slug) => {
                          if (slug) {
                            const slideIndex = osSlides.findIndex((s) => s.slug === slug);
                            if (slideIndex !== -1) api?.scrollTo(slideIndex);
                          }
                        }}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Amy" role="AI Dental Receptionist" image="/assets/amy.png" subtitle="Answers calls 24/7, and books patients directly into Asha PMS." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><AIReceptionistSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><IntelligentSchedulingSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Leena" role="Schedule Optimizer" image="/assets/leena.png" subtitle="The intelligent agent who proactively keeps your chairs full and production high." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><LenaSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><LenaWorkflowSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Stephanie" role="Hygiene Recall Specialist" image="/assets/stephanie.png" subtitle="Runs intelligent AI voice campaigns to reactivate patients and fill your hygiene schedule." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><HygieneRecallSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Kim" role="Case Acceptance Specialist" image="/assets/kim.png" subtitle="Turning unscheduled treatment plans into booked appointments and revenue." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><KimCaseAcceptanceSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Elise" role="Patient Experience Coordinator" image="/assets/elise.png" subtitle="Ensuring every patient feels heard and turning happy visits into 5-star reviews." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><ElisePatientExperienceSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Morgan" role="Patient Intake Specialist" image="/assets/morgan.png" subtitle="Ensures patients complete intake forms and provide insurance info before they arrive." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><MorganPatientIntakeSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Trisha" role="AI Practice Manager" image="/assets/trisha.png" subtitle="Monitors your entire AI workforce and assigns human follow-up only when necessary." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><PracticeManagerSlide /></CarouselItem>

                    {/* ═══ COMMUNICATION SUITE ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        label="Communications"
                        title="Communication"
                        titleAccent="Suite"
                        subtitle="Complete multi-channel communication tools for your practice"
                        icon={<MessageSquare className="w-8 h-8" />}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><VoIPSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><NextGenFormsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><CommunicationSuiteSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><EFAXSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><SMSCampaignsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><EmailCampaignsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PhysicalPhonesSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><TextToPaySlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><CardReadersSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><TeamChatSlide /></CarouselItem>

                    {/* ═══ REVENUE CYCLE AI TEAM ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <PMSTeamIntro2Slide
                        onAgentClick={(slug) => {
                          if (slug) {
                            const slideIndex = osSlides.findIndex((s) => s.slug === slug);
                            if (slideIndex !== -1) api?.scrollTo(slideIndex);
                          }
                        }}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Amanda" role="Insurance Verification Specialist" image="/assets/amanda.png" subtitle="The only AI that performs dual-source insurance verification to get you more accurate and comprehensive benefits breakdowns." />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><InsuranceVerificationSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><ComprehensiveBreakdownsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0">
                      <InsuranceCopilotSlide isActive={current === (osSlides.findIndex(s => s.slug === "amanda-copilot") + 1)} />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><BenefitFormsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><InsuranceCallingSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Ariel" role="AI Claims Expert" image="/assets/ariel.png" subtitle="Submitting clean claims with perfect clinical documentation instantly." theme="dark" />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><ArielSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AlainaSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Alisha" role="EOB Posting Specialist" image="/assets/alisha.png" subtitle="Posts explanation of benefits and insurance payments automatically." theme="dark" />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><EOBProcessingSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><PaymentReconciliationSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><EOBStreamliningSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide name="Kianna" role="Patient Billing" image="/assets/kianna.png" subtitle="Uses intelligent voice AI campaigns to help patients pay their balances." theme="dark" />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0"><PatientBillingSlide /></CarouselItem>

                    <CarouselItem className="h-full pl-0"><HumanBillerSlide /></CarouselItem>

                    {/* ═══ PRICING & PACKAGING ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        variant="blue"
                        label="Pricing"
                        title="Flexible Pricing"
                        subtitle="Simple, transparent pricing for your AI workforce"
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><PatientJourneySlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><SoftwareStackCostSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AIOSEmphasisSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AshaOperatingSystemSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><RCMPricingComparisonSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><TotalCostCalculatorSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><EngagementModelsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><ManagedServiceSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><FlexiblePlanSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><ROICalculatorSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AshaBasicallyFreeSlide /></CarouselItem>

                    {/* ═══ GETTING STARTED ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        label="Onboarding"
                        title="Getting"
                        titleAccent="Started"
                        subtitle="Simple steps to transform your practice today"
                        icon={<Rocket className="w-8 h-8" />}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><GoLiveTimelineSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><DataMigrationSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><ContractTermsSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><WhiteGloveOnboardingSlide /></CarouselItem>

                    {/* ═══ FAQ ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        variant="blue"
                        label="Support"
                        title="Common Questions"
                        subtitle="Everything you need to know about partnering with Asha"
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><FAQSlide /></CarouselItem>

                    {/* ═══ ROI & IMPACT ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        label="Results"
                        title="ROI &"
                        titleAccent="Impact"
                        subtitle="Real results from real dental practices"
                        icon={<BarChart3 className="w-8 h-8" />}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha automated staff tasks for"
                        titleHighlight="Dental Art Care"
                        imageType="logo"
                        image="/assets/practicelogos/dentalartcare.png"
                        stats={[
                          { label: "Total ROI", value: "$25,569" },
                          { label: "Production Increase", value: "$23,400" },
                          { label: "Tasks Completed", value: "1,428" },
                        ]}
                        description="In just a few weeks, Asha's AI team booked 16 new patients and 36 appointments for existing patients — capturing opportunities that would have been missed after hours. Our insurance agent completed 506 verifications and handled 62 calls, saving the team 668 minutes on the phone. Meanwhile, AI Fax Manager automatically categorized 54 incoming faxes, freeing up staff to focus on patient care."
                        roiCards={[
                          { label: "Total ROI", value: "$25,569", subtext: "production + labor savings" },
                          { label: "New Patients Booked", value: "16", subtext: "captured automatically" },
                          { label: "Staff Hours Saved", value: "11+", subtext: "from insurance calls alone" },
                          { label: "Existing Appts Booked", value: "36", subtext: "without staff involvement" },
                        ]}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha fueled growth for"
                        titleHighlight="Clovis Family Dentistry"
                        imageType="logo"
                        image="/assets/practicelogos/clovis.png"
                        stats={[
                          { label: "Total ROI", value: "$75,307" },
                          { label: "Production Increase", value: "$71,250" },
                          { label: "Tasks Completed", value: "2,642" },
                        ]}
                        description="In a few months, Asha's AI Receptionist captured 45 new patients and booked 107 appointments for existing patients — including many after-hours calls that would have gone unanswered. The AI also managed 910 patient calls, while the Hygiene Recall agent secured 16 appointments and completed 746 patient communications, keeping Clovis productive around the clock."
                        roiCards={[
                          { label: "Total ROI", value: "$75,307", subtext: "comprehensive impact" },
                          { label: "Production Increase", value: "$71,250", subtext: "from new & existing appts" },
                          { label: "New Patients", value: "45", subtext: "automatically booked" },
                          { label: "Calls Handled", value: "910", subtext: "by AI receptionist" },
                        ]}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha streamlined operations for"
                        titleHighlight="Dentures & Dentistry Spokane"
                        imageType="logo"
                        image="/assets/practicelogos/ddsspokane.png"
                        stats={[
                          { label: "Total ROI", value: "$14,766" },
                          { label: "Production Increase", value: "$13,250" },
                          { label: "Tasks Completed", value: "1,035" },
                        ]}
                        description="In a few months, Asha's AI Receptionist booked 10 new patients and 17 appointments for existing patients, expanding access to care. The Insurance Verification agent processed 494 verifications and uploaded 240 benefits forms automatically — streamlining administrative workflows and enabling staff to focus on patient care."
                        roiCards={[
                          { label: "Total ROI", value: "$14,766", subtext: "in a few months" },
                          { label: "New Patients", value: "10", subtext: "booked by AI" },
                          { label: "Verifications", value: "494", subtext: "processed automatically" },
                          { label: "Benefits Forms", value: "240", subtext: "auto-uploaded" },
                        ]}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha saved time for"
                        titleHighlight="St. Charles Dental Associates"
                        imageType="logo"
                        image="/assets/practicelogos/stcharles.png"
                        stats={[
                          { label: "Total ROI", value: "$38,450" },
                          { label: "Production Increase", value: "$35,100" },
                          { label: "Tasks Completed", value: "1,876" },
                        ]}
                        description="St. Charles saw major improvements across their practice with Asha's AI agents handling key tasks autonomously — from receptionist duties to insurance verification to patient recall campaigns."
                        roiCards={[
                          { label: "Total ROI", value: "$38,450", subtext: "comprehensive impact" },
                          { label: "Production Increase", value: "$35,100", subtext: "from AI-booked appointments" },
                          { label: "Tasks Completed", value: "1,876", subtext: "by AI agents" },
                          { label: "Staff Hours Saved", value: "15+", subtext: "per week" },
                        ]}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha elevated"
                        titleHighlight="Premier Dentistry"
                        imageType="logo"
                        image="/assets/practicelogos/premier.png"
                        stats={[
                          { label: "Total ROI", value: "$52,200" },
                          { label: "Production Increase", value: "$48,750" },
                          { label: "Tasks Completed", value: "2,134" },
                        ]}
                        description="Premier Dentistry experienced a significant boost in productivity and patient acquisition through Asha's comprehensive AI platform — seeing results across every department from front office to billing."
                        roiCards={[
                          { label: "Total ROI", value: "$52,200", subtext: "total impact" },
                          { label: "Production Increase", value: "$48,750", subtext: "new revenue" },
                          { label: "Tasks Completed", value: "2,134", subtext: "autonomous AI tasks" },
                          { label: "New Patients", value: "32", subtext: "captured by AI" },
                        ]}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha transformed operations for"
                        titleHighlight="Dental Arts of Hudson Valley"
                        imageType="logo"
                        image="/assets/practicelogos/hudsonvalley.png"
                        stats={[
                          { label: "Total ROI", value: "$41,800" },
                          { label: "Production Increase", value: "$38,500" },
                          { label: "Tasks Completed", value: "1,950" },
                        ]}
                        description="Dental Arts of Hudson Valley saw significant operational improvements with Asha handling patient communications, insurance verification, and scheduling optimization around the clock."
                        roiCards={[
                          { label: "Total ROI", value: "$41,800", subtext: "in production + savings" },
                          { label: "Production Increase", value: "$38,500", subtext: "from AI-driven growth" },
                          { label: "Tasks Completed", value: "1,950", subtext: "automated by AI" },
                          { label: "Hours Saved", value: "12+", subtext: "weekly staff time" },
                        ]}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha supported"
                        titleHighlight="NTX Mental Health"
                        imageType="logo"
                        image="/assets/practicelogos/ntx.png"
                        stats={[
                          { label: "Total ROI", value: "$18,900" },
                          { label: "Production Increase", value: "$16,200" },
                          { label: "Tasks Completed", value: "1,245" },
                        ]}
                        description="NTX Mental Health expanded their reach and reduced administrative burden using Asha's AI agents for patient communication, intake management, and appointment scheduling."
                        roiCards={[
                          { label: "Total ROI", value: "$18,900", subtext: "total practice impact" },
                          { label: "Production Increase", value: "$16,200", subtext: "captured revenue" },
                          { label: "Tasks", value: "1,245", subtext: "automated by AI" },
                          { label: "Patient Reach", value: "3x", subtext: "communication volume" },
                        ]}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><RealROISlide /></CarouselItem>

                    {/* ═══ COMPARISONS ═══ */}
                    <CarouselItem className="h-full pl-0">
                      <SectionTitleSlide
                        label="Compare"
                        title="How Asha"
                        titleAccent="Compares"
                        subtitle="See how Asha stacks up against point solutions"
                        icon={<GitCompare className="w-8 h-8" />}
                      />
                    </CarouselItem>

                    <CarouselItem className="h-full pl-0"><AshaVsDentinaSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AshaVsDentinaReceptionistSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AshaVsWeaveSlide /></CarouselItem>
                    <CarouselItem className="h-full pl-0"><AshaVsLassieSlide /></CarouselItem>

                  </CarouselContent>
                </div>
              </Carousel>
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={() => api?.scrollPrev()}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-white shadow-md border border-slate-200/50 opacity-0 group-hover:opacity-100 transition-all duration-200 z-20"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => api?.scrollNext()}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-white shadow-md border border-slate-200/50 opacity-0 group-hover:opacity-100 transition-all duration-200 z-20"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OS2SlideshowPage;
